import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface StarParticle {
  id: number;
  emoji: string;
  targetX: number;
  targetY: number;
  scale: number;
  rotate: number;
  delay: number;
}

interface CelebrationStarsProps {
  active: boolean;
  onReplay?: () => void;
}

const STAR_EMOJIS = ['⭐', '🌟', '✨', '💫', '🎉', '💖', '⭐', '🌟'];

export const CelebrationStars: React.FC<CelebrationStarsProps> = ({ active }) => {
  const [particles, setParticles] = useState<StarParticle[]>([]);

  useEffect(() => {
    if (active) {
      // Generate burst particles radiating outward in a 360 circle
      const count = 16;
      const newParticles: StarParticle[] = [];

      for (let i = 0; i < count; i++) {
        const angle = (i / count) * 2 * Math.PI + (Math.random() * 0.4 - 0.2);
        const distance = 90 + Math.random() * 90; // radius distance
        const targetX = Math.cos(angle) * distance;
        const targetY = Math.sin(angle) * distance;

        newParticles.push({
          id: i,
          emoji: STAR_EMOJIS[i % STAR_EMOJIS.length],
          targetX,
          targetY,
          scale: 0.8 + Math.random() * 0.6,
          rotate: (Math.random() - 0.5) * 720,
          delay: (i % 4) * 0.05,
        });
      }

      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  }, [active]);

  if (!active) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible flex items-center justify-center z-20">
      <AnimatePresence>
        {/* Soft rotating sunburst / golden glow aura */}
        <motion.div
          key="celebration-glow"
          initial={{ opacity: 0, scale: 0.2 }}
          animate={{ opacity: 0.5, scale: 1, rotate: 360 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{
            opacity: { duration: 1.0, ease: 'easeOut' },
            scale: { duration: 0.8, ease: 'easeOut' },
            rotate: { repeat: Infinity, duration: 25, ease: 'linear' },
          }}
          className="absolute w-80 h-80 rounded-full bg-radial from-amber-300/40 via-yellow-200/20 to-transparent blur-xl pointer-events-none"
        />

        {/* Celebration Banner Badge with 2-keyframe spring */}
        <motion.div
          key="celebration-banner"
          initial={{ scale: 0, y: -60, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 18, delay: 0.1 }}
          className="absolute -top-7 md:-top-9 bg-linear-to-r from-amber-400 via-yellow-300 to-amber-500 text-amber-950 font-display font-black text-sm md:text-base px-5 py-2 rounded-full shadow-lg border-2 border-amber-200 flex items-center gap-2 pointer-events-auto"
        >
          <motion.span
            animate={{ rotate: [-20, 20, -20] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
          >
            ⭐
          </motion.span>
          <span>HEBAT! KATA TERSUSUN!</span>
          <motion.span
            animate={{ rotate: [20, -20, 20] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
          >
            🎉
          </motion.span>
        </motion.div>

        {/* Bursting Stars and Sparkles with 2-keyframe spring */}
        {particles.map((p) => (
          <motion.div
            key={`celebration-star-${p.id}`}
            initial={{ x: 0, y: 0, scale: 0, opacity: 0, rotate: 0 }}
            animate={{
              x: p.targetX,
              y: p.targetY,
              scale: p.scale,
              opacity: 1,
              rotate: p.rotate,
            }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 220,
              damping: 14,
              delay: p.delay,
            }}
            className="absolute text-2xl md:text-3xl select-none filter drop-shadow-md"
          >
            <motion.div
              animate={{
                y: [-4, 4, -4],
                rotate: [-6, 6, -6],
              }}
              transition={{
                repeat: Infinity,
                duration: 2 + (p.id % 3) * 0.5,
                ease: 'easeInOut',
                delay: p.delay + 0.5,
              }}
            >
              {p.emoji}
            </motion.div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
